import * as React from 'react';
import './PVASelect.css';

export class PVASelectOption {
    id: number;
    label: string;
}

interface PVASelectProps {
    options: PVASelectOption[];
    selected: number | number[] | undefined;
    onChange: Function;
    defaultOptions?: Array<{ id: number; label?: string; value?: string }>;
    ignoreTopPosition?: boolean;
    stretch?: boolean;
    keepNoSelect?: boolean;
    noSelectTitle?: string;
    sortOptions?: (a: PVASelectOption, b: PVASelectOption) => number;
    typeaheadTimerInSeconds?: number;
    isMulti?: boolean;
    renderOption?: (option: PVASelectOption) => React.ReactNode;
    renderOptions?: () => React.ReactNode;
    disabled?: boolean;
}

interface PVASelectState {
    isOpen: boolean;
    typedSoFar: string;
    typeaheadTimerHandle?: NodeJS.Timer;
    typeaheadSelectedRow?: HTMLElement | null;
    refs: any[];
}

export class PVASelect extends React.Component<PVASelectProps, PVASelectState> {
    constructor(props: PVASelectProps) {
        super(props);

        this.state = {
            isOpen: false,
            typedSoFar: '',
            typeaheadTimerHandle: undefined,
            typeaheadSelectedRow: null,
            refs: [],
        };
    }

    private setRef = (element: any): any => {
        if (!element) return;
        const existingIndex = this.state.refs.findIndex((item: any) => item.id === element.id);
        if (existingIndex >= 0) {
            this.state.refs.splice(existingIndex, 1);
        }
        this.state.refs.push(element);
    }

    private selectedOpt() {
        if (this.props.selected === undefined || this.props.selected === null) {
            return null;
        }
        if (Array.isArray(this.props.selected)) {
            return null;
        }
        const opt = (this.props.options || []).find((u: PVASelectOption) => {
            return u.id === this.props.selected;
        });
        if (opt) {
            return opt;
        }
        if (this.props.defaultOptions) {
            const defOpt = this.props.defaultOptions.find((o) => o.id === this.props.selected);
            if (defOpt) {
                return {
                    id: defOpt.id,
                    label: this.displayLabel(defOpt)
                };
            }
        }
        return null;
    }

    private optChange(id: number) {
        if (this.props.onChange) {
            this.props.onChange({
                target: {
                    value: id
                }
            });
        }
        if (!this.props.isMulti || id <= 0) {
            this.setState({ isOpen: !this.state.isOpen });
        }
    }

    private onKeyPress = async (event: any) => {
        const updatedTypedSoFar = this.state.typedSoFar + event.key;
        await this.restartTypeaheadInterval();
        await this.setState({ ...this.state, typedSoFar: updatedTypedSoFar });
        if (!!this.state.typedSoFar) {
            const found = this.state.refs.find((element: HTMLElement) => {
                return element.innerText.toLowerCase().startsWith(this.state.typedSoFar.toLowerCase());
            });
            if (!!found) {
                found.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                await this.setState({ ...this.state, typeaheadSelectedRow: found });
            }
        }
    }

    private resetTypeahead = async () => {
        await this.setState({ ...this.state, typedSoFar: '' });
        await this.cancelTypeahead();
    }

    private cancelTypeahead = async () => {
        if (this.state.typeaheadTimerHandle !== undefined) {
            clearInterval(this.state.typeaheadTimerHandle);
            await this.setState({ ...this.state, typeaheadTimerHandle: undefined });
        }
    }

    private restartTypeaheadInterval = async () => {
        await this.cancelTypeahead();
        const seconds = this.props.typeaheadTimerInSeconds != undefined ? this.props.typeaheadTimerInSeconds : 0.5;
        await this.setState({ ...this.state, typeaheadTimerHandle: setInterval(this.resetTypeahead, seconds * 1000) });
    }

    private defaultSort = (a: PVASelectOption, b: PVASelectOption): number => {
        if (a.label > b.label) return 1;
        if (a.label < b.label) return -1;
        return 0;
    }

    private displayLabel = (item: { label?: string; value?: string }): string => {
        return (item.label != null ? item.label : (item.value != null ? item.value : '')) as string;
    }

    render() {
        const selectedOpt = this.selectedOpt();
        const sortFn = this.props.sortOptions || this.defaultSort;
        const options = (this.props.options || []).slice().sort(sortFn);
        const selectedCount = Array.isArray(this.props.selected) ? this.props.selected.length : 0;

        return (
            <div className={`component-PVASelect${this.props.isMulti ? ' multi' : ''}`}>
                <div className={`pva-select-box ${this.props.stretch ? 'stretch' : ''} ${this.props.disabled ? 'disable-control' : ''}`} tabIndex={this.props.disabled ? -1 : 0} onKeyPress={this.props.disabled ? undefined : this.onKeyPress} onClick={(e) => !this.props.disabled && this.setState({ isOpen: !this.state.isOpen })}>
                    <div className={`pva-select-box-selected ${this.props.ignoreTopPosition === true ? 'ignore-top-position' : ''} ${this.state.isOpen ? 'open' : ''}`}>
                        {this.props.isMulti && <span>{selectedCount} {this.props.noSelectTitle || 'Items'}</span>}
                        {!this.props.isMulti && selectedOpt && <span>{selectedOpt.label}</span>}
                        {!this.props.isMulti && !selectedOpt && <span>{this.props.noSelectTitle ? this.props.noSelectTitle : '- Select -'}</span>}
                        <div className="pva-select-box-toggle">
                            {!this.state.isOpen && <span className="glyphicon glyphicon-triangle-bottom" />}
                            {this.state.isOpen && <span className="glyphicon glyphicon-triangle-top" />}
                        </div>
                    </div>
                </div>
                {this.state.isOpen && <div className="pva-select-box-options">
                    {this.props.renderOptions ? this.props.renderOptions() : (
                        <React.Fragment>
                            {this.props.keepNoSelect && <div className="option" onClick={(e) => this.optChange(-1)}>
                                <span>{this.props.noSelectTitle ? this.props.noSelectTitle : '- Select -'}</span>
                            </div>}
                            {this.props.defaultOptions && this.props.defaultOptions.map((o: any, ind: number) => {
                                return <div key={`select-default-opt-${ind}`} className="option" onClick={(e) => this.optChange(o.id)}>
                                    <span>{this.displayLabel(o)}</span>
                                </div>;
                            })}
                            {options.map((data: PVASelectOption, index: number) => {
                                const isHighlighted = (`select-opt-${data.id}` === (this.state.typeaheadSelectedRow == null ? '' : this.state.typeaheadSelectedRow.id));
                                const isChecked = this.props.isMulti && Array.isArray(this.props.selected) && this.props.selected.indexOf(data.id) > -1;

                                return (
                                    <div id={`select-opt-${data.id}`} key={`select-opt-${data.id}`} ref={this.setRef} className={`option${isHighlighted ? ' highlighted' : ''}`} onClick={(e) => this.optChange(data.id)}>
                                        {this.props.isMulti && (
                                            <div className={`pva-checkbox ${isChecked ? 'checked' : ''}`}>
                                                <div className="checkbox-inner" />
                                            </div>
                                        )}
                                        {this.props.renderOption ? this.props.renderOption(data) : <span>{data.label}</span>}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    )}
                </div>}
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/reports/blocks/PVASelect.tsx